import React, { Suspense, useEffect } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Route, useLocation } from "react-router-dom";
import { Routes } from "@datadog/browser-rum-react/react-router-v6";
import { useAuth } from "oidc-react";
import Login from "./pages/Login";
import { routesConfig } from "services/routesConfig";
import type { AppRoute } from "services/routesConfig";
import { workspaceStatusPageSx } from "components/common/workspaceStyles";

interface ProtectedRouteProps {
  component: AppRoute["component"];
  onLoginStatusChange: (status: boolean) => void;
}

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
}

function RouteLoadingFallback() {
  return (
    <Box sx={workspaceStatusPageSx}>
      <Stack spacing={1.5} alignItems="center" color="text.secondary">
        <CircularProgress size={28} />
        <Typography variant="body2">Loading workspace...</Typography>
      </Stack>
    </Box>
  );
}

function RouteErrorFallback() {
  return (
    <Box sx={workspaceStatusPageSx}>
      <Stack spacing={1} alignItems="center" color="text.secondary">
        <Typography variant="h6" color="text.primary">
          Unable to load this page
        </Typography>
        <Typography variant="body2">Refresh the page or try again.</Typography>
      </Stack>
    </Box>
  );
}

class RouteErrorBoundary extends React.Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error("Router -> RouteErrorBoundary: Unable to load route", error);
  }

  render() {
    if (this.state.hasError) {
      return <RouteErrorFallback />;
    }

    return this.props.children;
  }
}

function RouteBoundary({ children, resetKey }: { children: React.ReactNode; resetKey: string }) {
  return (
    <RouteErrorBoundary key={resetKey}>
      <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ component: Component, onLoginStatusChange }) => {
  const auth = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    onLoginStatusChange(Boolean(auth.userData));
  }, [auth.userData, onLoginStatusChange]);

  return auth.userData ? (
    <RouteBoundary resetKey={pathname}>
      <Component />
    </RouteBoundary>
  ) : (
    <Login />
  );
};

interface RouterProps {
  onLoginStatusChange: (status: boolean) => void;
}

function Router({ onLoginStatusChange }: RouterProps) {
  const { pathname } = useLocation();

  return (
    <Routes>
      {routesConfig.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            route.isPublic ? (
              <RouteBoundary resetKey={pathname}>{React.createElement(route.component)}</RouteBoundary>
            ) : (
              <ProtectedRoute component={route.component} onLoginStatusChange={onLoginStatusChange} />
            )
          }
        />
      ))}
    </Routes>
  );
}

export default Router;
